import type { GroupBase, ValueContainerProps } from 'react-select';
import { components as ReactSelectComponents } from 'react-select';
import { useTheme } from '@trinity/chat-components/components/Theme';

export type NamedItem = { id: string | number; name: string };

/**
 * Shared multi-select `ValueContainer`: collapses a selection to "first.name +N others"
 * instead of stacking every chip. Bypassed while typing so the input text still shows.
 */
export function CollapsedValueContainer<T extends NamedItem>(
  props: ValueContainerProps<T, true, GroupBase<T>>
) {
  const { children, getValue, selectProps } = props;
  const values = getValue();
  const inputChild = Array.isArray(children) ? children[children.length - 1] : null;
  const isSearching = Boolean(selectProps.inputValue);
  const theme = useTheme();

  if (values.length === 0 || isSearching) {
    return <ReactSelectComponents.ValueContainer {...props}>{children}</ReactSelectComponents.ValueContainer>;
  }

  const [first, ...rest] = values;
  const label =
    rest.length > 0 ? `${first.name} +${rest.length} other${rest.length > 1 ? 's' : ''}` : first.name;

  return (
    <ReactSelectComponents.ValueContainer {...props}>
      {/* Font matches Presentation.buildSelectStyles (Published Date/Project/Tags) for consistency */}
      <span
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          fontFamily: theme.fonts.regular,
        }}
      >
        {label}
      </span>
      {inputChild}
    </ReactSelectComponents.ValueContainer>
  );
}
